import { defineStore } from 'pinia';
import type { ContexteSaisieDto, PeriodeDto } from '@igd/shared';
import { api } from '@/lib/api';
import type { ReferentielsDto } from '@/lib/types';

interface PeriodeConnue {
  id: string;
  libelle: string;
  dateDebut?: string;
}

interface State {
  saisie: ContexteSaisieDto | null;
  referentiels: ReferentielsDto | null;
  /** Périodes rencontrées (contexte, référentiels, tableaux RH, admin), par id. */
  periodesConnues: Record<string, PeriodeConnue>;
}

/**
 * LE CONTEXTE de travail : barème du jour, périodes ouvertes, réglages et
 * référentiels. Chargé une fois et partagé : le formulaire, le tableau de bord
 * et les filtres en ont tous besoin, et le recharger à chaque écran ferait
 * clignoter les listes déroulantes.
 *
 * Il n'existe pas de route « toutes les périodes » ouverte à tous : on
 * AGRÈGE celles que les différentes réponses laissent voir, pour que le
 * sélecteur de période propose au moins tout ce que l'utilisateur a croisé.
 */
export const useContexteStore = defineStore('contexte', {
  state: (): State => ({ saisie: null, referentiels: null, periodesConnues: {} }),
  getters: {
    aujourdhui: (s): string => s.saisie?.aujourdhui ?? new Date().toISOString().slice(0, 10),
    /** Options du sélecteur de période, les plus récentes d'abord. */
    optionsPeriodes: (s): Array<{ value: string; label: string }> =>
      Object.values(s.periodesConnues)
        .sort((a, b) => (b.dateDebut ?? b.libelle).localeCompare(a.dateDebut ?? a.libelle))
        .map((p) => ({ value: p.id, label: p.libelle })),
  },
  actions: {
    retenirPeriodes(periodes: PeriodeConnue[]): void {
      for (const p of periodes) {
        if (!p.id) continue;
        const deja = this.periodesConnues[p.id];
        this.periodesConnues[p.id] = { id: p.id, libelle: p.libelle, dateDebut: p.dateDebut ?? deja?.dateDebut };
      }
    },
    retenirPeriodesDto(periodes: Array<PeriodeDto | null | undefined>): void {
      this.retenirPeriodes(
        periodes.filter((p): p is PeriodeDto => !!p).map((p) => ({ id: p.id, libelle: p.libelle, dateDebut: p.dateDebut })),
      );
    },

    async chargerSaisie(force = false): Promise<ContexteSaisieDto> {
      if (this.saisie && !force) return this.saisie;
      const saisie = await api.get<ContexteSaisieDto>('/contexte-saisie');
      this.saisie = saisie;
      this.retenirPeriodesDto(saisie.periodesOuvertes);
      return saisie;
    },

    async chargerReferentiels(force = false): Promise<ReferentielsDto> {
      if (this.referentiels && !force) return this.referentiels;
      let ref: ReferentielsDto;
      try {
        ref = await api.get<ReferentielsDto>('/referentiels');
        this.retenirPeriodesDto(ref.periodes);
      } catch {
        // Non bloquant : les filtres se contenteront des périodes déjà connues.
        ref = { societes: [], managers: [], periodes: [] };
      }
      this.referentiels = ref;
      return ref;
    },

    /** La période ouverte qui couvre une date (rattachement d'une déclaration). */
    periodePour(date: string): PeriodeDto | null {
      if (!date) return null;
      return this.saisie?.periodesOuvertes.find((p) => date >= p.dateDebut && date <= p.dateFin) ?? null;
    },

    vider(): void {
      this.saisie = null;
      this.referentiels = null;
      this.periodesConnues = {};
    },
  },
});
